import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Lock, AlertCircle, CheckCircle2, Pencil, X, 
  Wrench, Car, Search, MessageSquare, ChevronRight, ShieldCheck,
} from 'lucide-react';

/**
 * CustomerIntentModal - Mandatory entry point for FOH to configure customer intent
 * 
 * This modal blocks access to the Pricing Builder until FOH configures:
 * 1. Customer Visit Purpose (Intent Mode) - required
 * 2. Work already agreed with customer (£ value) - required  
 * 3. Communication Style - required
 * 
 * CRITICAL: Intent config locks after first customer-facing output is generated.
 */

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Intent Mode Options
const INTENT_MODES = [
  { 
    id: 'mot', 
    label: 'MOT',
    description: 'Vehicle booked for MOT only — pricing failures/advisories',
    icon: ShieldCheck,
    color: 'teal'
  },
  { 
    id: 'service', 
    label: 'Service',
    description: 'Vehicle booked for scheduled service',
    icon: Wrench,
    color: 'emerald'
  },
  { 
    id: 'mot_service', 
    label: 'MOT + Service',
    description: 'Vehicle booked for MOT and service together',
    icon: Car,
    color: 'blue'
  },
  { 
    id: 'diagnostic', 
    label: 'Diagnostic',
    description: 'Vehicle booked due to a reported issue',
    icon: Search,
    color: 'amber'
  }
];

// Communication Style Options
const COMMUNICATION_STYLES = [
  { 
    id: 'reassuring', 
    label: 'Reassuring',
    description: 'Softer phrasing, light explanations',
    example: '"Replacing this will help complete your service properly..."'
  },
  { 
    id: 'neutral', 
    label: 'Neutral',
    description: 'Clear and professional (default)',
    example: '"Replacement is required to complete your service correctly."'
  },
  { 
    id: 'direct', 
    label: 'Direct',
    description: 'Short, action-focused',
    example: '"Replacement required to complete service."'
  }
];

// Quick-fill buttons - editable helpers, not fixed rules
// MOT: No VAT on MOTs. Diagnostic: £125 + VAT = £150
const QUICK_FILL_OPTIONS = [
  { id: 'service', label: 'Service', defaultValue: 150.00 },
  { id: 'mot_class4', label: 'MOT (Class 4)', defaultValue: 54.85 },
  { id: 'mot_class7', label: 'MOT (Class 7)', defaultValue: 58.60 },
  { id: 'diagnostic', label: 'Diagnostic', defaultValue: 150.00 },
  { id: 'other', label: 'Other', defaultValue: 0.00 }
];

const CustomerIntentModal = ({
  isOpen,
  onConfirm,
  onClose,
  currentConfig = null,
  isChangeMode = false,
  isLocked = false,
  yestimateId = null
}) => {
  // State for each required field
  const [intentMode, setIntentMode] = useState(currentConfig?.intent_mode || '');
  const [inputValue, setInputValue] = useState(
    currentConfig?.already_authorised_value !== undefined 
      ? currentConfig.already_authorised_value.toString() 
      : ''
  );
  
  // Evaluate arithmetic expressions like "245+54.85" or "150 + 54.85 + 40"
  const evaluateExpression = (expr) => {
    if (!expr || expr.trim() === '') return 0;
    try {
      // Only allow digits, decimal points, +, -, spaces
      const sanitized = expr.replace(/[^0-9.+\-\s]/g, '');
      if (!sanitized.trim()) return 0;
      // Split by + or - while keeping the operator
      const tokens = sanitized.match(/[+-]?\s*[\d.]+/g);
      if (!tokens) return 0;
      const total = tokens.reduce((sum, t) => sum + parseFloat(t.trim()), 0);
      return isNaN(total) ? 0 : Math.round(total * 100) / 100;
    } catch {
      return 0;
    }
  };
  
  const evaluatedTotal = evaluateExpression(inputValue);
  const hasExpression = inputValue.includes('+') || inputValue.includes('-');
  const [communicationStyle, setCommunicationStyle] = useState(
    currentConfig?.communication_style || 'neutral'
  );
  const [selectedTags, setSelectedTags] = useState(
    currentConfig?.already_authorised_tags || []
  );
  const [step, setStep] = useState(1); // 1: Intent, 2: Value, 3: Style
  const [quickFillValues, setQuickFillValues] = useState({});
  
  // Load quick-fill values from backend
  useEffect(() => {
    fetch(`${API}/intent-mode/quick-fill-options`)
      .then(res => res.json())
      .then(data => {
        const values = {};
        data.options?.forEach(opt => {
          values[opt.id] = opt.value;
        });
        setQuickFillValues(values);
      })
      .catch(err => console.log('Using default quick-fill values'));
  }, []);

  // Validation
  const canProceedStep1 = intentMode !== '';
  const canProceedStep2 = inputValue !== '' && evaluatedTotal >= 0;
  const canProceedStep3 = communicationStyle !== '';
  const canConfirm = canProceedStep1 && canProceedStep2 && canProceedStep3;

  const handleQuickFill = (optionId) => {
    const value = quickFillValues[optionId] ?? QUICK_FILL_OPTIONS.find(o => o.id === optionId)?.defaultValue ?? 0;
    
    // ADDITIVE: append +value to existing input (allows building totals)
    // If input is empty or "0", replace. Otherwise append with +
    if (!inputValue || inputValue.trim() === '' || inputValue.trim() === '0' || inputValue.trim() === '0.00') {
      setInputValue(value.toString());
    } else if (value > 0) {
      setInputValue(`${inputValue}+${value}`);
    }
    
    // Map MOT variants to mot_service intent
    if (step === 1) {
      if (optionId === 'service') setIntentMode('service');
      else if (optionId === 'mot_class4' || optionId === 'mot_class7') setIntentMode('mot_service');
      else if (optionId === 'diagnostic') setIntentMode('diagnostic');
    }
    
    // Add tag (normalise MOT variants to single 'mot' tag)
    const tagId = optionId.startsWith('mot_') ? 'mot' : optionId;
    if (!selectedTags.includes(tagId)) {
      setSelectedTags([...selectedTags, tagId]);
    }
  };

  const handleTagToggle = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };

  const handleConfirm = () => {
    const value = evaluatedTotal;
    onConfirm({
      intent_mode: intentMode,
      communication_style: communicationStyle,
      already_authorised_value: value,
      already_authorised_tags: selectedTags,
      confirmed_zero: value === 0
    });
  };

  const handleClose = () => {
    // Always allow dismissal — the parent decides whether to re-show.
    // Previously this was gated on isChangeMode which made the X button
    // visually present but functionally dead in first-time-config mode.
    if (onClose) {
      onClose();
    }
  };

  const getIntentModeColor = (modeId) => {
    const mode = INTENT_MODES.find(m => m.id === modeId);
    return mode?.color || 'slate';
  };

  // Locked state - show read-only view
  if (isLocked && !isChangeMode) {
    return (
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[500px] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Lock className="h-5 w-5 text-amber-600" />
              Intent Configuration Locked
            </DialogTitle>
            <DialogDescription className="text-slate-600 text-sm mt-2">
              This configuration was locked when customer-facing output was generated.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500 mb-1">Visit Purpose</p>
              <p className="font-semibold text-slate-800">
                {INTENT_MODES.find(m => m.id === currentConfig?.intent_mode)?.label || currentConfig?.intent_mode}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500 mb-1">Already Authorised</p>
              <p className="font-semibold text-slate-800">£{(currentConfig?.already_authorised_value || 0).toFixed(2)}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500 mb-1">Communication Style</p>
              <p className="font-semibold text-slate-800">
                {COMMUNICATION_STYLES.find(s => s.id === currentConfig?.communication_style)?.label || currentConfig?.communication_style}
              </p>
            </div>
          </div>
          {onClose && (
            <Button onClick={onClose} className="mt-4 w-full">
              Close
            </Button>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && onClose) handleClose(); }}>
      <DialogContent 
        className="sm:max-w-[600px] bg-white max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => { if (!isChangeMode) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!isChangeMode) e.preventDefault(); else handleClose(); }}
      >
        {/* Close button — always available so the FOH is never trapped
            in this modal. The shadcn DialogContent renders its own X but
            its onOpenChange route was previously gated on isChangeMode,
            making the X look interactive but do nothing on first-time
            config. We now render an explicit, always-clickable X. */}
        {onClose && (
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 z-10"
            data-testid="intent-modal-close-btn"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            {isChangeMode ? 'Update Customer Intent' : 'Configure Customer Intent'}
          </DialogTitle>
          <DialogDescription className="text-slate-600 text-sm mt-2">
            This information shapes how work is presented to the customer.
            All three fields are required.
          </DialogDescription>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center gap-2 mt-4 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  step === s 
                    ? 'bg-blue-600 text-white' 
                    : step > s 
                      ? 'bg-green-500 text-white' 
                      : 'bg-slate-200 text-slate-500'
                }`}
              >
                {step > s ? <CheckCircle2 className="h-5 w-5" /> : s}
              </div>
              {s < 3 && <div className={`flex-1 h-1 rounded ${step > s ? 'bg-green-500' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        <div className="space-y-6">
          {/* STEP 1: Customer Visit Purpose */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-800 mb-1">Customer Visit Purpose</h3>
                <p className="text-sm text-slate-500">Why was the vehicle booked in?</p>
              </div>

              <div className="space-y-3">
                {INTENT_MODES.map((mode) => {
                  const Icon = mode.icon;
                  const isSelected = intentMode === mode.id;
                  return (
                    <button
                      key={mode.id}
                      onClick={() => setIntentMode(mode.id)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        isSelected 
                          ? `border-${mode.color}-500 bg-${mode.color}-50` 
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      data-testid={`intent-mode-${mode.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? `bg-${mode.color}-500 text-white` : 'bg-slate-100 text-slate-600'
                        }`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <p className={`font-semibold ${isSelected ? `text-${mode.color}-900` : 'text-slate-800'}`}>
                            {mode.label}
                          </p>
                          <p className={`text-sm ${isSelected ? `text-${mode.color}-700` : 'text-slate-500'}`}>
                            {mode.description}
                          </p>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className={`h-5 w-5 text-${mode.color}-600`} />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <Button 
                onClick={() => setStep(2)}
                disabled={!canProceedStep1}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="intent-step1-next"
              >
                Continue <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}

          {/* STEP 2: Work Already Agreed */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-800 mb-1">Work Already Agreed</h3>
                <p className="text-sm text-slate-500">
                  What value of work has already been agreed with the customer?
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">What should be included?</p>
                    <p className="text-blue-700">
                      Include diagnostics, servicing, MOT-related work, or any other work 
                      authorised prior to this inspection.
                    </p>
                    <p className="text-blue-700 mt-2 font-medium">
                      Enter £0.00 if nothing has yet been agreed.
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick-fill Buttons — additive: click to add to total */}
              <div>
                <p className="text-xs text-slate-500 mb-2">Click to add to total:</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_FILL_OPTIONS.filter(o => o.id !== 'other').map(opt => {
                    const val = quickFillValues[opt.id] ?? opt.defaultValue;
                    const isMotItem = opt.id.startsWith('mot_');
                    return (
                      <Button
                        key={opt.id}
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickFill(opt.id)}
                        className="text-sm"
                        data-testid={`quick-fill-${opt.id}`}
                      >
                        {opt.label} {val > 0 && <span className="ml-1 text-emerald-700 font-semibold">£{val.toFixed(2)}</span>}
                        {isMotItem && <span className="ml-1 text-xs text-slate-400">(No VAT)</span>}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Amount Input */}
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-slate-700">£</span>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="0.00 or 54.85+150"
                    className="w-56 text-xl font-semibold"
                    autoFocus
                    data-testid="authorised-value-input"
                  />
                  {inputValue && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setInputValue(''); setSelectedTags([]); }}
                      className="text-slate-400 hover:text-slate-600 text-xs px-2"
                      data-testid="clear-value-btn"
                    >
                      Clear
                    </Button>
                  )}
                </div>
                {hasExpression && evaluatedTotal > 0 && (
                  <div className="mt-2 ml-9 flex items-center gap-2" data-testid="expression-total">
                    <span className="text-sm text-slate-500">=</span>
                    <span className="text-lg font-bold text-emerald-700">£{evaluatedTotal.toFixed(2)}</span>
                  </div>
                )}
                <p className="mt-1 ml-9 text-xs text-slate-400">Use + to add multiple amounts (e.g. 54.85+150)</p>
              </div>

              {/* Tags */}
              <div>
                <p className="text-xs text-slate-500 mb-2">What does this include?</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'service', label: 'Service' },
                    { id: 'mot', label: 'MOT' },
                    { id: 'diagnostic', label: 'Diagnostic' },
                    { id: 'other', label: 'Other' }
                  ].map(tag => (
                    <Badge
                      key={tag.id}
                      variant={selectedTags.includes(tag.id) ? 'default' : 'outline'}
                      className={`cursor-pointer transition-all ${
                        selectedTags.includes(tag.id)
                          ? 'bg-emerald-600 hover:bg-emerald-700'
                          : 'hover:bg-slate-100'
                      }`}
                      onClick={() => handleTagToggle(tag.id)}
                      data-testid={`tag-${tag.id}`}
                    >
                      {tag.label}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(3)}
                  disabled={!canProceedStep2}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="intent-step2-next"
                >
                  Continue <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: Communication Style */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-800 mb-1">Communication Style</h3>
                <p className="text-sm text-slate-500">
                  How should information be presented to this customer?
                </p>
              </div>

              <div className="space-y-3">
                {COMMUNICATION_STYLES.map((style) => {
                  const isSelected = communicationStyle === style.id;
                  return (
                    <button
                      key={style.id}
                      onClick={() => setCommunicationStyle(style.id)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      data-testid={`comm-style-${style.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                        }`}>
                          {isSelected && <CheckCircle2 className="h-3 w-3 text-white" />}
                        </div>
                        <div className="flex-1">
                          <p className={`font-semibold ${isSelected ? 'text-blue-900' : 'text-slate-800'}`}>
                            {style.label}
                          </p>
                          <p className={`text-sm ${isSelected ? 'text-blue-700' : 'text-slate-500'}`}>
                            {style.description}
                          </p>
                          <p className="text-xs text-slate-400 mt-1 italic">
                            {style.example}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Summary */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <h4 className="font-medium text-slate-700 text-sm">Summary</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Purpose</p>
                    <p className="font-semibold text-slate-800">
                      {INTENT_MODES.find(m => m.id === intentMode)?.label}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Agreed Value</p>
                    <p className="font-semibold text-slate-800">
                      £{evaluatedTotal.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Style</p>
                    <p className="font-semibold text-slate-800">
                      {COMMUNICATION_STYLES.find(s => s.id === communicationStyle)?.label}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleConfirm}
                  disabled={!canConfirm}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="intent-confirm-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Confirm Configuration
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * CustomerIntentDisplay - Shows configured intent in a compact display
 */
export const CustomerIntentDisplay = ({
  config,
  onChangeClick,
  compact = false,
  locked = false
}) => {
  if (!config) return null;

  const intentLabel = INTENT_MODES.find(m => m.id === config.intent_mode)?.label || config.intent_mode;
  const styleLabel = COMMUNICATION_STYLES.find(s => s.id === config.communication_style)?.label || config.communication_style;

  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          {intentLabel}
        </Badge>
        <span className="text-slate-400">|</span>
        <div className="flex items-center gap-1">
          <Lock className="h-3 w-3 text-emerald-600" />
          <span className="text-sm font-semibold text-emerald-700">
            £{(config.already_authorised_value || 0).toFixed(2)}
          </span>
        </div>
        <span className="text-slate-400">|</span>
        <Badge variant="outline" className="text-slate-600">
          {styleLabel}
        </Badge>
        {!locked && onChangeClick && (
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-500 hover:text-slate-700 h-6 px-2"
            onClick={onChangeClick}
            data-testid="change-intent-config-btn"
          >
            <Pencil className="h-3 w-3" />
          </Button>
        )}
        {locked && <Lock className="h-3 w-3 text-amber-500" />}
      </div>
    );
  }
  
  return (
    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-slate-700 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Customer Intent Configuration
          {locked && <Badge variant="secondary" className="text-xs">Locked</Badge>}
        </h4>
        {!locked && onChangeClick && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onChangeClick}
            className="text-slate-500 hover:text-slate-700"
            data-testid="change-intent-config-btn"
          >
            <Pencil className="h-4 w-4 mr-1" /> Change
          </Button>
        )}
      </div>
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div>
          <p className="text-slate-500 mb-1">Visit Purpose</p>
          <Badge className={`bg-${INTENT_MODES.find(m => m.id === config.intent_mode)?.color || 'slate'}-100 text-${INTENT_MODES.find(m => m.id === config.intent_mode)?.color || 'slate'}-800`}>
            {intentLabel}
          </Badge>
        </div>
        <div>
          <p className="text-slate-500 mb-1">Already Authorised</p>
          <p className="font-bold text-lg text-emerald-700">
            £{(config.already_authorised_value || 0).toFixed(2)}
          </p>
        </div>
        <div>
          <p className="text-slate-500 mb-1">Communication Style</p>
          <Badge variant="outline">{styleLabel}</Badge>
        </div>
      </div>
    </div>
  );
};

export default CustomerIntentModal;
